import { useState } from 'react';
import { avatarTone, initialsOf } from '../utils/dealChatThread';

/**
 * One person, drawn as a face.
 * ─────────────────────────────────────────────────────────────────────
 * There were two of these. Layout.jsx had an `AvatarDisk` that split a
 * name on whitespace and took the first letter of the first two words,
 * on a `#5BBFEF` disk for everybody; the deal chat used `initialsOf`
 * (first and last) on a tone derived from the person's id. So the same
 * colleague was a different colour and sometimes different letters
 * depending on which part of the app you were looking at — and the chat
 * version carries a comment explaining exactly why a single shared
 * colour is the wrong answer:
 *
 *   "Grey initials on grey squares make everybody look the same, which
 *    is the one thing an avatar exists not to do."
 *
 * That reasoning wins, so this uses the chat's helpers and Layout now
 * calls this instead of its own.
 *
 * THE FALLBACK IS THE POINT. These photos are Google account pictures
 * on lh3.googleusercontent.com, and those URLs rotate: a src that
 * worked last month returns 403 and the browser draws its broken-image
 * glyph. On a screen whose whole job is "who is on this deal", a torn
 * page icon beside somebody's name is worse than never having tried.
 * So a load failure falls back to the initials, which is also what the
 * one profile with no photo at all gets — of fourteen people, thirteen
 * have one, and the fourteenth is a real colleague who would otherwise
 * be the only broken tile on the page.
 */
export default function Avatar({
  name,
  email = '',
  avatarUrl = '',
  seed,
  size = 32,
  square = false,
  ring = null,
  // The presence stack colours each person by their live map-cursor
  // colour, which carries meaning this component should not overrule.
  // Left unset, the tone is derived from identity.
  color = null,
  border = 'none',
  title,
  style,
}) {
  // WHICH url failed, not whether one did. A new src then gets a fresh
  // attempt for free: without that, one 403 would keep showing initials
  // even after the person updated their photo. Storing a boolean and
  // clearing it in an effect does the same job with a cascading render
  // and a lint error.
  const [failedUrl, setFailedUrl] = useState('');

  const label = String(name || email || '').trim();
  // Tone is keyed to identity, not to the display name, so somebody
  // does not change colour when their name is edited.
  const tone = avatarTone(seed || email || label);
  const showImage = !!avatarUrl && failedUrl !== avatarUrl;

  const base = {
    width: size,
    height: size,
    borderRadius: square ? 0 : '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    boxSizing: 'border-box',
    background: color || tone.bg,
    color: color ? '#0f172a' : tone.fg,
    border,
    fontSize: `${Math.max(9, Math.round(size * 0.38))}px`,
    fontWeight: 700,
    letterSpacing: '0.2px',
    lineHeight: 1,
    userSelect: 'none',
    ...(ring ? { boxShadow: `0 0 0 2px ${ring}` } : null),
    ...style,
  };

  return (
    <span title={title || label || undefined} style={base} aria-hidden={title ? undefined : 'true'}>
      {showImage ? (
        <img
          src={avatarUrl}
          alt={title ? '' : label}
          // Google 403s a referred request for these, so send none.
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(avatarUrl)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (initialsOf(label) || '?')}
    </span>
  );
}
